import { Button, Card, Stack, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import { IAITextQuestionnaire } from 'api';
import { EFKT_SITE_URL } from 'api/http';

import { TextAreaInputField } from 'components/v2/input/TextAreaInputField';
import RadioSelect from 'components/v2/radio/RadioSelect';
import ShowSpin from 'components/v2/spin/ShowSpin';
import { FormInterviewState } from 'constants/enums/status';
import { useInterview } from 'hooks/services/useInterview';
import { useLocalization } from 'localization';

export interface QuestionnaireRef {
  submit: () => Promise<boolean>;
}

interface QuestionnaireProps {
  onError?: () => void;
  taskId?: string;
  setIsSubmitting?: (isSubmitting: boolean) => void;
}

const Questionnaire = forwardRef<QuestionnaireRef, QuestionnaireProps>(({ taskId, onError, setIsSubmitting }, ref) => {
  const { t } = useLocalization();

  const { action, apiState, control, options, formState, setValue } = useInterview<IAITextQuestionnaire>({
    isQuestionnaire: true,
    taskId,
    onError,
    setIsSubmitting,
  });

  useImperativeHandle(ref, () => ({
    submit: action.onSubmit,
  }));

  const pendingView = (
    <>
      {apiState?.questionData?.questions.map(({ question, yesOrNo }, index) => (
        <>
          {yesOrNo ? (
            <>
              <Typography variant="h5">{question}</Typography>
              <RadioSelect
                options={options.map(option => ({
                  label: option,
                  value: option,
                }))}
                defaultValue={options[0]}
                onChange={value => setValue(`answers.${index}.answer`, value)}
              />
            </>
          ) : (
            <TextAreaInputField key={question} control={control} label={question} name={`answers[${index}].answer`} />
          )}
        </>
      ))}
    </>
  );

  const views = {
    [FormInterviewState.ALREADY_SUBMITTED]: (
      <>
        <Typography variant="caption">{t('users.pages.interview.alreadySubmitted')}</Typography>
        <Button variant="contained" onClick={() => open(EFKT_SITE_URL, '_self')} loading={apiState.isSubmitLoading}>
          {t('common.terms.backButton')}
        </Button>
      </>
    ),
    [FormInterviewState.ERROR]: <Typography>{t('users.pages.interview.notFound')}</Typography>,
    [FormInterviewState.SUCCESS]: (
      <Stack>
        <Typography>{t('users.pages.interview.successTitle')}</Typography>
        <Typography>{t('users.pages.interview.successDescription')}</Typography>
      </Stack>
    ),
    [FormInterviewState.PENDING]: (
      <>
        {pendingView}
        <Button variant="contained" onClick={action.onSubmit} loading={apiState.isSubmitLoading}>
          {t('common.terms.submit')}
        </Button>
      </>
    ),
    [FormInterviewState.EMPTY]: null,
  };

  // usage within the modal
  if (taskId) {
    return pendingView;
  }

  return (
    <Stack justifyContent="center" alignItems="center">
      <ShowSpin when={apiState.isQuestionLoading}>
        <Card
          sx={{
            mt: '2rem',
            mb: '2rem',
            minWidth: '50%',
            width: 'fit-content',
          }}
        >
          <Typography variant="h4">{t('users.pages.interview.title')}</Typography>
          {views[formState]}
        </Card>
      </ShowSpin>
    </Stack>
  );
});

Questionnaire.displayName = 'Questionnaire';

export default Questionnaire;
